import { useEffect, useState } from 'react';
import { api, ApiError, type OrgSummary, type Seats as SeatsData } from '../api.ts';

/**
 * Seats against the contracted ceiling.
 *
 * `limit === null` means uncontracted, not unlimited-by-design — it is what a
 * care home looks like before anyone has agreed a number with them. Saying
 * "no limit set" rather than showing an infinity symbol keeps that distinction
 * visible to whoever has to chase it.
 */
export function Seats({ org }: { org: OrgSummary }) {
  const [data, setData] = useState<SeatsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<SeatsData>(`/api/orgs/${org.id}/seats`)
      .then(setData)
      .catch((e) =>
        setError(
          e instanceof ApiError && e.status === 403
            ? 'Seats and billing are visible to an owner, admin or billing contact.'
            : e instanceof ApiError ? e.message : String(e),
        ),
      );
  }, [org.id]);

  if (error) {
    return (
      <p className="rounded-xl border border-dashed border-(--line) px-4 py-8 text-center text-(--muted)">
        {error}
      </p>
    );
  }
  if (!data) return <p className="text-(--muted)">Loading…</p>;

  const pct = data.limit ? Math.min(100, Math.round((data.used / data.limit) * 100)) : null;
  const tight = pct !== null && pct >= 90;

  return (
    <section className="space-y-6">
      <div className="rounded-xl border border-(--line) bg-(--panel) p-5">
        <h2 className="mb-1 text-lg font-semibold">Seats</h2>
        <p className="mb-4 text-sm text-(--muted)">
          One seat per resident whose record this organisation owns. Residents
          you only have <em>access</em> to — a family keeps the record — do not
          use a seat.
        </p>

        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-semibold">{data.used}</span>
          <span className="text-(--muted)">
            {data.limit === null ? 'residents · no seat limit set' : `of ${data.limit} seats`}
          </span>
        </div>

        {pct !== null && (
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-(--line)">
            <div
              className={`h-full rounded-full ${tight ? 'bg-amber-500' : 'bg-(--accent)'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        {data.limit === null && (
          <p className="mt-3 rounded-lg border border-(--line) px-3 py-2 text-sm text-(--muted)">
            No ceiling is contracted, so nothing is enforced. That is usually a
            setup left unfinished rather than a decision.
          </p>
        )}

        <p className="mt-3 text-sm text-(--muted)">
          Need more?{' '}
          <a className="text-(--accent)" href="mailto:hello@drbell.app?subject=More%20seats">
            hello@drbell.app
          </a>
          . Raising the limit takes effect immediately — nobody has to wait for
          a renewal.
        </p>

        {tight && (
          <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
            {data.used >= (data.limit ?? 0)
              ? 'Every seat is in use. The next resident will be refused until the limit is raised.'
              : `Only ${(data.limit ?? 0) - data.used} seat(s) left.`}
          </p>
        )}
      </div>

      {/*
        How staff get in.
        
        The commonest question a care-home manager asks after signing up, and
        it had no answer anywhere in the product: they invite a nurse, she
        gets nothing they can see, and they email support. Saying it here
        costs a paragraph.
      */}
      <div className="rounded-xl border border-(--line) p-5">
        <h2 className="mb-3 text-lg font-semibold">How your staff sign in</h2>
        <ol className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-(--accent-soft) text-xs font-bold text-(--accent)">1</span>
            <span>
              Invite them on the <strong>Staff</strong> tab, by the mobile
              number they actually use. An invitation is held against that
              number and works on no other.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-(--accent-soft) text-xs font-bold text-(--accent)">2</span>
            <span>
              They open <a className="text-(--accent)" href="https://org.drbell.app">org.drbell.app</a>{' '}
              and sign in with that number. A six-digit code arrives by SMS —
              no password to choose, forget, or write on a whiteboard.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-(--accent-soft) text-xs font-bold text-(--accent)">3</span>
            <span>
              They accept the invitation. <strong>Until they accept they see
              nothing</strong> — an invitation on its own grants no access,
              which is why a nurse who has been invited but not accepted is
              not yet in the escalation path.
            </span>
          </li>
        </ol>
        <p className="mt-4 rounded-lg border border-(--line) bg-(--panel) px-3 py-2 text-sm text-(--muted)">
          Staff on shared ward terminals should use <strong>Sign out</strong>{' '}
          when they finish. A session lasts 90 days, so the next person to sit
          down is otherwise signed in as them — with their name against
          anything recorded.
        </p>
      </div>

      <div className="rounded-xl border border-(--line) p-5">
        <h2 className="mb-3 text-lg font-semibold">Subscription</h2>
        {data.subscription ? (
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[10rem_1fr]">
            <dt className="text-(--muted)">Plan</dt>
            <dd className="font-medium">{data.subscription.tier}</dd>
            <dt className="text-(--muted)">Status</dt>
            <dd>{data.subscription.status}</dd>
            <dt className="text-(--muted)">Contracted seats</dt>
            <dd>{data.subscription.seats ?? '—'}</dd>
            <dt className="text-(--muted)">Renews</dt>
            <dd>
              {data.subscription.currentPeriodEnd
                ? new Date(data.subscription.currentPeriodEnd).toLocaleDateString()
                : '—'}
            </dd>
          </dl>
        ) : (
          <p className="text-sm text-(--muted)">
            No subscription on file. Organisations are invoiced rather than
            charged through an app store, so this is set by DrBell when the
            contract is agreed.
          </p>
        )}
      </div>
    </section>
  );
}
